/**
 * The pixel maths behind the editor.
 *
 * Deliberately free of the DOM: no canvas, no ImageData, no window. It takes an
 * RGBA byte array and mutates it, which is what a canvas hands over anyway, and
 * it means the maths is testable in Node rather than only observable by eye.
 *
 * One implementation serves both the live preview and the export. The obvious
 * alternative — CSS filters for the preview, because they are free, and a pixel
 * pass for the export, because CSS cannot express warmth — would have meant two
 * different renderers and a saved photograph that did not match the one you
 * approved. Whatever the preview shows, the export repeats exactly, because it
 * is the same function over more pixels.
 */

export interface Adjustments {
  /** Stops of exposure, as -100..100 mapping to -1..+1 stops. */
  exposure: number;
  /** -100 flattens to mid grey, +100 doubles the distance from it. */
  contrast: number;
  /** Negative recovers a blown sky; positive opens it up further. */
  highlights: number;
  /** Positive lifts what is buried in the dark. */
  shadows: number;
  /** -100 is fully grey, +100 is twice as colourful. */
  saturation: number;
  /** Negative cools towards blue, positive warms towards amber. */
  warmth: number;
  monochrome: boolean;
}

export const NEUTRAL: Adjustments = {
  exposure: 0,
  contrast: 0,
  highlights: 0,
  shadows: 0,
  saturation: 0,
  warmth: 0,
  monochrome: false,
};

/** Whether this would change anything, so an untouched photo can skip the pass. */
export function isNeutral(adjustments: Adjustments): boolean {
  return (
    adjustments.exposure === 0 &&
    adjustments.contrast === 0 &&
    adjustments.highlights === 0 &&
    adjustments.shadows === 0 &&
    adjustments.saturation === 0 &&
    adjustments.warmth === 0 &&
    !adjustments.monochrome
  );
}

/*
 * Rec. 709 luma weights — the same ones the sRGB standard uses, and the reason
 * a desaturated red does not come out the same grey as a desaturated green.
 * Averaging the three channels instead is the classic mistake: it makes skies
 * too bright and skin too dark.
 */
const LUMA_R = 0.2126;
const LUMA_G = 0.7152;
const LUMA_B = 0.0722;

/**
 * The tone curve as a 256-entry lookup.
 *
 * Exposure, shadows, highlights and contrast are each a function of one channel
 * value and nothing else, so they can be resolved once for every possible byte
 * rather than recomputed for every pixel. On a twelve-megapixel photograph that
 * is 256 evaluations instead of thirty-six million.
 *
 * Exported because it is the interesting half to test: a curve is much easier
 * to make assertions about than a bitmap.
 */
export function toneCurve(adjustments: Adjustments): Uint8ClampedArray {
  const curve = new Uint8ClampedArray(256);

  // -100..100 to -1..+1 stops, then to a multiplier.
  const exposure = 2 ** (adjustments.exposure / 100);
  const contrast = (adjustments.contrast + 100) / 100;
  const highlights = adjustments.highlights / 100;
  const shadows = adjustments.shadows / 100;

  for (let index = 0; index < 256; index++) {
    let value = (index / 255) * exposure;

    /*
     * Shadows and highlights are weighted by where the pixel already sits, so
     * lifting shadows leaves a bright sky alone and recovering highlights
     * leaves the dark alone. Squaring the distance keeps each one to its own
     * end of the range instead of washing over the midtones.
     *
     * Per channel rather than per luminance. A proper implementation weights by
     * the pixel's brightness, which cannot be known from one channel — but that
     * costs the lookup table, and the difference is small enough that it is not
     * worth thirty-six million extra multiplications.
     */
    if (shadows !== 0) {
      const mask = (1 - value) * (1 - value);
      value += shadows * mask * 0.5;
    }
    if (highlights !== 0) {
      const mask = value * value;
      value += highlights * mask * 0.5;
    }

    // Contrast pivots on mid grey, so it stretches away from the middle
    // rather than brightening everything.
    value = (value - 0.5) * contrast + 0.5;

    curve[index] = Math.round(value * 255);
  }

  return curve;
}

/**
 * Apply an adjustment set to RGBA bytes, in place.
 *
 * Alpha is left exactly as found: every one of these operations is about
 * colour, and a photograph that gains or loses transparency because somebody
 * moved an exposure slider would be a bug, not an effect.
 */
export function applyAdjustments(
  pixels: Uint8ClampedArray,
  adjustments: Adjustments,
): void {
  if (isNeutral(adjustments)) return;

  const curve = toneCurve(adjustments);

  // Warmth trades red against blue, which is what a colour temperature does to
  // daylight. Green is the axis it turns about, so it is left alone.
  const warmth = adjustments.warmth / 100;
  const redScale = 1 + warmth * 0.3;
  const blueScale = 1 - warmth * 0.3;

  const saturation = (adjustments.saturation + 100) / 100;
  const { monochrome } = adjustments;

  for (let index = 0; index < pixels.length; index += 4) {
    /*
     * Non-null assertions rather than `?? 0`.
     *
     * The loop is bounded by the array's own length and steps in fours, so
     * these four reads are in range by construction. Under `noUncheckedIndexedAccess`
     * the compiler cannot see that, and a `?? 0` on the hot path would be a
     * branch per channel per pixel to handle a case that cannot happen.
     */
    let red = curve[pixels[index]!]!;
    let green = curve[pixels[index + 1]!]!;
    let blue = curve[pixels[index + 2]!]!;

    if (warmth !== 0) {
      red *= redScale;
      blue *= blueScale;
    }

    if (monochrome) {
      const luma = red * LUMA_R + green * LUMA_G + blue * LUMA_B;
      red = luma;
      green = luma;
      blue = luma;
    } else if (saturation !== 1) {
      const luma = red * LUMA_R + green * LUMA_G + blue * LUMA_B;
      red = luma + (red - luma) * saturation;
      green = luma + (green - luma) * saturation;
      blue = luma + (blue - luma) * saturation;
    }

    // Uint8ClampedArray rounds and clamps to 0..255 on write, which is exactly
    // the behaviour wanted here and the reason nothing above bothers to.
    pixels[index] = red;
    pixels[index + 1] = green;
    pixels[index + 2] = blue;
  }
}

/**
 * A one-line summary of what was changed, for the interface to show.
 *
 * "Exposure, warmth" beside a reset button says more than a count does, and it
 * is the difference between trusting the button and wondering what it will undo.
 */
export function describeAdjustments(adjustments: Adjustments): string[] {
  const changed: string[] = [];
  if (adjustments.exposure !== 0) changed.push("Exposure");
  if (adjustments.contrast !== 0) changed.push("Contrast");
  if (adjustments.highlights !== 0) changed.push("Highlights");
  if (adjustments.shadows !== 0) changed.push("Shadows");
  if (adjustments.saturation !== 0) changed.push("Saturation");
  if (adjustments.warmth !== 0) changed.push("Warmth");
  if (adjustments.monochrome) changed.push("Black and white");
  return changed;
}
